import { useEffect } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';
import Stack from '@zenbusiness/zui/Stack';
import dayjs from 'dayjs';

import { REFRESH_AFTER_INSTANT_DEPOSIT } from '#client/constants/eventName';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useCheckTransactionContext from '#client/hooks/useCheckTransactionContext';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useEventListeners from '#client/hooks/useEventListeners';
import useSkeleton from '#client/hooks/useSkeleton';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { GET_STATISTICS_MONEY_IN_OUT } from '#client/queries';

import SectionTitle from '../SectionTitle';
import MoneyTrend from './components/MoneyTrend';

const DEFAULT_DAYS = '30';

export default function FinanceTrends() {
  const toDate = dayjs().endOf('d');
  const fromDate = dayjs().subtract(DEFAULT_DAYS, 'd').startOf('d');
  const fromToDate = `${fromDate.format('MMM DD')} - ${toDate.format('MMM DD')}`;

  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { createCommonErrorToast } = useToastContext();
  const { loading, hasTransaction, error: transactionError } = useCheckTransactionContext();

  const {
    unitAccountId,
    isValidMfa,
    isEnabled,
    isEnrolled,
    isFetchingMfaStatus,
    loading: loadingUnitAccountId
  } = useUnitAccountId();

  const { isOpeningModal } = useCheckOpenModal();
  const isSkipQuery =
    isFetchingMfaStatus ||
    !isEnrolled ||
    !isEnabled ||
    !isValidMfa ||
    !unitAccountId ||
    !businessEntityUuid ||
    isOpeningModal ||
    !hasTransaction;

  const {
    loading: loadingStatistics,
    error,
    refetch: refetchStatistics,
    data: statisticsData
  } = useQuery(GET_STATISTICS_MONEY_IN_OUT, {
    variables: {
      params: {
        businessEntityUuid,
        dates: {
          from: fromDate,
          to: toDate
        }
      }
    },
    skip: isSkipQuery,
    noThrow: true,
    skipCache: true
  });

  useError(error);

  useEventListeners([
    {
      eventName: REFRESH_AFTER_INSTANT_DEPOSIT,
      eventHandler: refetchStatistics
    }
  ]);

  const { totalMoneyIn, totalMoneyOut } = (hasTransaction && statisticsData?.statistics) || {};
  const isHideSkeleton =
    error || (!loadingStatistics && !loadingUnitAccountId) || transactionError || !loading;
  const [showSkeleton] = useSkeleton(isHideSkeleton);

  useEffect(() => {
    const errorMessage = error || transactionError;
    if (errorMessage) {
      createCommonErrorToast({ errorMessage });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, transactionError]);

  return (
    <Stack
      gap={{ xs: '8px', md: '12px' }}
      marginBottom={{ xs: '8px', md: '12px' }}
    >
      <SectionTitle>Finance Trends</SectionTitle>
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={{ xs: '8px', md: '12px' }}
        width="100%"
      >
        <MoneyTrend
          amount={totalMoneyIn}
          description={fromToDate}
          loading={showSkeleton}
          srcIcon="/icon/micro/credit-fill.svg"
          title="Money In"
        />
        <MoneyTrend
          amount={totalMoneyOut}
          description={fromToDate}
          loading={showSkeleton}
          srcIcon="/icon/micro/debit-fill.svg"
          title="Money Out"
        />
      </Stack>
    </Stack>
  );
}
